import React, { useEffect, useState } from 'react';
import Calculator from './decoy/Calculator';
import Clock from './decoy/Clock';
import Converter from './decoy/Converter';
import {
  shouldShowDecoy, decoyMode, markDecoyAway, clearDecoyAway,
} from '../lib/decoy';

/**
 * What stands in front of Arcanum.
 *
 * Two rules make this work, and both are easy to get subtly wrong.
 *
 * **It decides on the first render, not in an effect.** An effect runs after
 * the first paint, so a glimpse of the gate would appear before the calculator
 * replaced it — and one frame of Arcanum is the whole feature failing. Hence
 * the lazy initialiser rather than useEffect.
 *
 * **It replaces the app rather than covering it.** While the disguise is up,
 * nothing of Arcanum is mounted: no routes, no sockets, no requests. An overlay
 * would leave the real app running and fetching underneath, which is a
 * different and much weaker promise than this one.
 *
 * On a short absence this stays out of the way and AppLock handles it — asking
 * for a password is faster than solving a puzzle, and a disguise that appears
 * every time you glance at a message gets switched off within a week.
 */
const Decoy = ({ children }) => {
  const [showing, setShowing] = useState(() => shouldShowDecoy(true));

  useEffect(() => {
    const sync = () => {
      if (document.hidden) {
        markDecoyAway();
        return;
      }
      if (shouldShowDecoy(false)) setShowing(true);
    };

    document.addEventListener('visibilitychange', sync);
    window.addEventListener('pageshow', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  if (!showing) return children;

  const open = () => {
    clearDecoyAway();
    setShowing(false);
  };

  // A case per disguise, written out rather than implied, so adding one is
  // adding a line and not rewriting this.
  switch (decoyMode()) {
    case 'calculator':
      return <Calculator onOpen={open} />;
    case 'clock':
      return <Clock onOpen={open} />;
    case 'converter':
      return <Converter onOpen={open} />;
    default:
      // Armed with a mode nobody recognises: open normally rather than trap
      // someone behind a screen that cannot be rendered.
      return children;
  }
};

export default Decoy;
